/**
 * FolderSidebar — renders the folder tree with matters nested under each
 * folder, plus an Unfiled section for matters in zero folders. Used as
 * the primary matter-navigation sidebar in Workspace.
 *
 * Open/closed state per-folder is per-workstation, persisted to
 * localStorage keyed by client code.
 *
 * Matter selection: clicking a matter sets the selected matter ID; the
 * folder ID it was clicked under is also captured (URL state in
 * Workspace, not here).
 *
 * Folder management: hover a folder row → see + (add child) / pencil
 * (rename) / trash icons. Hover a matter row → see folder icon to
 * open the picker.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, Folder as FolderIcon, FolderPlus, Pencil, Trash2, Plus, Sparkles, Archive, RotateCcw } from 'lucide-react'
import { folders as foldersApi, type FolderNode, type Matter } from '../api'
import { useFlashOnChange } from './useFlashOnChange'

type Props = {
  matters: Matter[]
  selectedMatterId: number | 'inbox' | null
  selectedFolderId: number | null
  onSelectMatter: (matterId: number, folderId: number | null) => void
  onArchiveMatter?: (matterId: number) => void
  /** Total recursive matter count for "all matters" (drives the optional "All" entry). */
  clientCode: string
  /** True when the user has typed in the matter search box. We auto-expand
   *  folders that contain matching matters so the results are visible.
   *  When search clears, folders return to their pre-search state because
   *  the search-driven expansion is derived (not persisted) — only the
   *  user's manual toggles touch localStorage. If during search the user
   *  clicks a matter, the selection effect adds its ancestors to the
   *  persisted openSet, so those folders stay open after clearing search. */
  searchActive?: boolean
  /** Set of matter ids that the user has already "seen" — initial value
   *  is whatever existed at page load. A brand-new row (id not in this
   *  set) flashes on first mount; the row calls markSeen() afterward so
   *  a subsequent unmount/remount (folder collapse/expand) doesn't
   *  re-fire. Null means "snapshot not yet captured" — treat everything
   *  as already-seen for now. */
  seenAtLoad?: Set<number> | null
  /** Callback to mark a matter id as "seen" once its flash has played.
   *  Called from MatterRowCompact when isNew=true and the row mounts. */
  markSeen?: (id: number) => void
}

const FOLDER_OPEN_KEY_PREFIX = 'me.folderOpen.'
const UNFILED_OPEN_KEY_PREFIX = 'me.unfiledOpen.'

function loadOpenSet(clientCode: string): Set<number> {
  try {
    const raw = localStorage.getItem(FOLDER_OPEN_KEY_PREFIX + clientCode)
    if (!raw) return new Set()
    const arr = JSON.parse(raw)
    return new Set(Array.isArray(arr) ? arr.filter((n) => typeof n === 'number') : [])
  } catch { return new Set() }
}

function saveOpenSet(clientCode: string, openSet: Set<number>): void {
  try {
    localStorage.setItem(FOLDER_OPEN_KEY_PREFIX + clientCode, JSON.stringify([...openSet]))
  } catch { /* ignore */ }
}

export default function FolderSidebar({
  matters, selectedMatterId, selectedFolderId, onSelectMatter, onArchiveMatter, clientCode,
  searchActive, seenAtLoad, markSeen,
}: Props) {
  const tree = useQuery({
    queryKey: ['folders', 'tree'],
    queryFn: () => foldersApi.tree(),
    staleTime: 30_000,
    // Poll every 20s so folder counts and AI auto-folder placements
    // appear without requiring a manual refresh. ETag-equivalent comes
    // from the cache — react-query won't re-render unless the response
    // payload genuinely differs.
    refetchInterval: 20_000,
  })

  // clientCode arrives async (after /api/auth/me resolves). Initial render
  // can have it empty. Naive useState(() => loadFromStorage(clientCode))
  // would read the WRONG key on first render, then immediately overwrite
  // the right key with the wrong (default) value — wiping persisted state.
  // Fix: track which clientCode we've initialized for via a ref, and only
  // (re)read when it changes. Save only after init completes.
  const initializedFor = useRef<string | null>(null)
  const [openSet, setOpenSet] = useState<Set<number>>(new Set())
  const [unfiledOpen, setUnfiledOpen] = useState<boolean>(true)

  useEffect(() => {
    if (!clientCode) return
    if (initializedFor.current === clientCode) return
    initializedFor.current = clientCode
    setOpenSet(loadOpenSet(clientCode))
    try {
      const v = localStorage.getItem(UNFILED_OPEN_KEY_PREFIX + clientCode)
      setUnfiledOpen(v === null ? true : v === '1')
    } catch { /* default true already set */ }
  }, [clientCode])

  useEffect(() => {
    if (!clientCode || initializedFor.current !== clientCode) return
    saveOpenSet(clientCode, openSet)
  }, [clientCode, openSet])

  useEffect(() => {
    if (!clientCode || initializedFor.current !== clientCode) return
    try {
      localStorage.setItem(UNFILED_OPEN_KEY_PREFIX + clientCode, unfiledOpen ? '1' : '0')
    } catch { /* ignore */ }
  }, [clientCode, unfiledOpen])

  const toggle = (folderId: number) => {
    setOpenSet((prev) => {
      const next = new Set(prev)
      if (next.has(folderId)) next.delete(folderId); else next.add(folderId)
      return next
    })
  }

  // Search auto-expansion: when the user has typed in the matter
  // search box, Workspace passes the filtered matters list AND
  // searchActive=true. We compute the set of folder IDs that contain
  // any of the displayed matters (walking up to the root) and union
  // with the persisted openSet for display. This expansion is NEVER
  // persisted — clearing the search returns folders to their pre-
  // search state automatically. If the user clicks a matter while
  // searching, the selection-effect adds those ancestors to the
  // persisted set, so they stay open after clearing.
  const searchExpansion = useMemo(() => {
    if (!searchActive) return new Set<number>()
    const expanded = new Set<number>()
    const folderMap = new Map<number, FolderNode>()
    const indexNodes = (nodes: FolderNode[]) => {
      for (const n of nodes) {
        folderMap.set(n.id, n)
        if (n.children) indexNodes(n.children)
      }
    }
    indexNodes(tree.data || [])
    for (const m of matters) {
      if (m.status === 'closed') continue
      for (const a of m.folder_assignments || []) {
        let fid: number | null = a.folder
        while (fid != null) {
          expanded.add(fid)
          const parent: number | null | undefined = folderMap.get(fid)?.parent_folder
          fid = parent ?? null
        }
      }
    }
    return expanded
  }, [searchActive, matters, tree.data])

  // Combined display set passed to FolderRow. User toggles still only
  // touch openSet (persisted) — searchExpansion is a transient overlay.
  const displayedOpenSet = useMemo(() => {
    if (!searchActive) return openSet
    const out = new Set(openSet)
    for (const id of searchExpansion) out.add(id)
    return out
  }, [openSet, searchExpansion, searchActive])

  // Per-folder DISTINCT-matter recursive counts. Server-side
  // matter_count_recursive sums children's matter_count_self, which
  // double-counts a matter that's in both a parent folder AND one of
  // its sub-folders. This client-side computation builds a
  // Set<matter_id> per folder (self + all descendants), so the count
  // is the size of the union — each matter counted once per subtree
  // regardless of how many sub-folders it appears in.
  //
  // Active for both search and non-search paths so the count chip is
  // always accurate. During search, `matters` is already filtered by
  // Workspace, so the distinct count naturally reflects the filter.
  const folderDistinctMatters = useMemo<Map<number, Set<number>>>(() => {
    const out = new Map<number, Set<number>>()
    const folderMap = new Map<number, FolderNode>()
    const indexNodes = (nodes: FolderNode[]) => {
      for (const n of nodes) {
        folderMap.set(n.id, n)
        if (n.children) indexNodes(n.children)
      }
    }
    indexNodes(tree.data || [])
    for (const m of matters) {
      if (m.status === 'closed') continue
      for (const a of m.folder_assignments || []) {
        let fid: number | null = a.folder
        while (fid != null) {
          let set = out.get(fid)
          if (!set) { set = new Set(); out.set(fid, set) }
          set.add(m.id)
          const parent: number | null | undefined = folderMap.get(fid)?.parent_folder
          fid = parent ?? null
        }
      }
    }
    return out
  }, [matters, tree.data])

  // Unfiled section auto-opens when search is active AND has unfiled
  // results. User's persisted preference is restored when search clears.
  const displayedUnfiledOpen = searchActive ? true : unfiledOpen

  // Build a Map<folderId, Matter[]> for fast lookup. Each matter appears
  // under each folder it's filed in.
  const mattersByFolder = useMemo(() => {
    const out: Map<number, Matter[]> = new Map()
    const unfiled: Matter[] = []
    for (const m of matters) {
      if (m.status === 'closed') continue  // closed mirror handled separately
      const assignments = m.folder_assignments ?? []
      if (assignments.length === 0) {
        unfiled.push(m)
        continue
      }
      for (const a of assignments) {
        const arr = out.get(a.folder) || []
        arr.push(m)
        out.set(a.folder, arr)
      }
    }
    return { byFolder: out, unfiled }
  }, [matters])

  // Auto-expand folders containing the selected matter (so refresh / deep-link
  // lands on a visible row). Only fires when selectedMatterId or selectedFolderId
  // changes — won't override user's manual collapses on idle.
  useEffect(() => {
    if (selectedFolderId == null || tree.data == null) return
    setOpenSet((prev) => {
      const next = new Set(prev)
      // Walk up from selectedFolderId to root and open every ancestor.
      const allFolders = flattenTree(tree.data!)
      let current = allFolders.find((f) => f.id === selectedFolderId)
      while (current) {
        next.add(current.id)
        const parent = allFolders.find((f) => f.id === current!.parent_folder)
        current = parent
      }
      return next
    })
  }, [selectedFolderId, tree.data])

  const qc = useQueryClient()
  const create = useMutation({
    mutationFn: (payload: { name: string; parent_folder?: number | null; description?: string }) =>
      foldersApi.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folders', 'tree'] }),
  })

  const [newTopOpen, setNewTopOpen] = useState(false)

  // Distinct count across all folders + unfiled. Sum of root recursive
  // counts would double-count matters filed in multiple top-level
  // folders, so we union all folder sets at the root level.
  // IMPORTANT: this useMemo must run on every render BEFORE any early
  // returns below — Rules of Hooks. Compute roots inline here.
  const roots = tree.data || []
  const total = useMemo(() => {
    const all = new Set<number>()
    for (const root of roots) {
      const set = folderDistinctMatters.get(root.id)
      if (set) for (const id of set) all.add(id)
    }
    for (const m of mattersByFolder.unfiled) all.add(m.id)
    return all.size
  }, [roots, folderDistinctMatters, mattersByFolder.unfiled])

  if (tree.isLoading) {
    return <div className="px-2 py-3 text-[11px] me-text-mute">Loading folders…</div>
  }
  if (tree.isError) {
    return <div className="px-2 py-3 text-[11px] me-text-mute">Couldn't load folders.</div>
  }

  return (
    <div className="space-y-0.5">
      <div className="me-section-h flex items-center justify-between">
        <span>Folders: {total.toLocaleString()} matter{total === 1 ? '' : 's'}</span>
        <button
          onClick={() => setNewTopOpen((v) => !v)}
          className="me-text-mute hover:me-text inline-flex items-center gap-0.5 text-[10px] normal-case font-normal"
          title="New top-level folder"
        >
          <FolderPlus size={10} /> new
        </button>
      </div>

      {newTopOpen && (
        <NewFolderInline
          parentId={null}
          onSubmit={(name) => { create.mutate({ name }); setNewTopOpen(false) }}
          onCancel={() => setNewTopOpen(false)}
        />
      )}

      {/* Unfiled pinned to the very top (user preference — surfaces
          unsorted matters before any organized folders), then top-level
          folders below. Inside any folder, sub-folders come before
          matters; at the top level we make an exception for Unfiled. */}
      {mattersByFolder.unfiled.length > 0 && (
        <UnfiledSection
          matters={mattersByFolder.unfiled}
          selectedMatterId={selectedMatterId}
          selectedFolderId={selectedFolderId}
          onSelectMatter={onSelectMatter}
          onArchiveMatter={onArchiveMatter}
          isOpen={displayedUnfiledOpen}
          onToggle={() => setUnfiledOpen((v) => !v)}
          seenAtLoad={seenAtLoad}
          markSeen={markSeen}
        />
      )}

      {roots.map((root) => (
        <FolderRow
          key={root.id}
          node={root}
          openSet={displayedOpenSet}
          onToggle={toggle}
          mattersByFolder={mattersByFolder.byFolder}
          selectedMatterId={selectedMatterId}
          selectedFolderId={selectedFolderId}
          onSelectMatter={onSelectMatter}
          onArchiveMatter={onArchiveMatter}
          isTop
          searchActive={searchActive}
          folderDistinctMatters={folderDistinctMatters}
          seenAtLoad={seenAtLoad}
          markSeen={markSeen}
        />
      ))}
    </div>
  )
}

function flattenTree(roots: FolderNode[]): FolderNode[] {
  const out: FolderNode[] = []
  function walk(nodes: FolderNode[]) {
    for (const n of nodes) {
      out.push(n)
      if (n.children?.length) walk(n.children)
    }
  }
  walk(roots)
  return out
}

function UnfiledSection({
  matters, selectedMatterId, selectedFolderId, onSelectMatter, onArchiveMatter,
  isOpen, onToggle, seenAtLoad, markSeen,
}: {
  matters: Matter[]
  selectedMatterId: number | 'inbox' | null
  selectedFolderId: number | null
  onSelectMatter: (matterId: number, folderId: number | null) => void
  onArchiveMatter?: (matterId: number) => void
  isOpen: boolean
  onToggle: () => void
  seenAtLoad?: Set<number> | null
  markSeen?: (id: number) => void
}) {
  // Outer-div + inner-button structure mirrors FolderRow exactly so the
  // chevron's left edge aligns with top-level folders. A direct
  // <button> wrapper renders the chevron with a slight horizontal
  // offset on some browsers (button display + flex interaction).
  return (
    <div className="mb-0.5">
      <div
        className="w-full flex items-center justify-between pl-0 pr-1 py-1 rounded-md hover:bg-[color:var(--me-hover)]"
        title={`${matters.length} matter${matters.length === 1 ? '' : 's'} not in any folder`}
      >
        <button
          onClick={onToggle}
          className="flex items-center gap-1 flex-1 min-w-0"
        >
          <ChevronRight size={11} className={`transition-transform ${isOpen ? 'rotate-90' : ''} me-text-mute shrink-0`} />
          <FolderIcon size={12} className="me-text-mute shrink-0" />
          <span className="font-medium me-text text-[12px]">Unfiled</span>
        </button>
        <span className="me-chip me-mono text-[10px]">{matters.length.toLocaleString()}</span>
      </div>
      {isOpen && (
        <div className="mt-0.5 pl-3">
          {matters.map((m) => (
            <MatterRowCompact
              key={m.id}
              matter={m}
              selected={selectedMatterId === m.id && selectedFolderId === null}
              onSelect={() => onSelectMatter(m.id, null)}
              onArchive={onArchiveMatter ? () => onArchiveMatter(m.id) : undefined}
              isNew={seenAtLoad != null && !seenAtLoad.has(m.id)}
              markSeen={markSeen}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function FolderRow({
  node, openSet, onToggle, mattersByFolder, selectedMatterId, selectedFolderId, onSelectMatter, onArchiveMatter, isTop,
  searchActive, folderDistinctMatters, seenAtLoad, markSeen,
}: {
  node: FolderNode
  openSet: Set<number>
  onToggle: (id: number) => void
  mattersByFolder: Map<number, Matter[]>
  selectedMatterId: number | 'inbox' | null
  selectedFolderId: number | null
  onSelectMatter: (matterId: number, folderId: number | null) => void
  onArchiveMatter?: (matterId: number) => void
  /** True for top-level rows. Removes left padding so the chevron sits
   *  flush with the sidebar edge (saves horizontal space which is
   *  precious in a folder-tree sidebar). */
  isTop?: boolean
  /** When searching, dim folders with 0 hits to opacity-50 so the
   *  tree structure stays visible but the empty-result state is
   *  clear. The chip count itself comes from folderDistinctMatters
   *  which is filter-aware (matters prop is filtered upstream). */
  searchActive?: boolean
  /** Per-folder Set of distinct matter IDs (self + descendants).
   *  Replaces server-side matter_count_recursive which double-counts
   *  matters filed in a parent folder AND one of its sub-folders. */
  folderDistinctMatters?: Map<number, Set<number>>
  /** Page-load snapshot used to flag rows that appeared after the page
   *  mounted. Forwarded down to children + leaf rows. */
  seenAtLoad?: Set<number> | null
  /** Callback that promotes an id into seenAtLoad after its row has
   *  flashed once. Forwarded down to children + leaf rows. */
  markSeen?: (id: number) => void
}) {
  const isOpen = openSet.has(node.id)
  const matters = mattersByFolder.get(node.id) || []
  const displayedCount = folderDistinctMatters?.get(node.id)?.size ?? 0
  const dimmed = searchActive && displayedCount === 0
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(node.name)
  const [newSubOpen, setNewSubOpen] = useState(false)

  const update = useMutation({
    mutationFn: (payload: { name?: string; description?: string }) => foldersApi.update(node.id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folders', 'tree'] })
      qc.invalidateQueries({ queryKey: ['matters'] })
    },
  })
  const remove = useMutation({
    mutationFn: () => foldersApi.remove(node.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folders', 'tree'] })
      // Also invalidate matters — a deleted folder removes its M2M rows,
      // and matters losing their last folder become "Unfiled" in the
      // sidebar render.
      qc.invalidateQueries({ queryKey: ['matters'] })
    },
  })
  const create = useMutation({
    mutationFn: (payload: { name: string }) =>
      foldersApi.create({ name: payload.name, parent_folder: node.id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folders', 'tree'] }),
  })
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)

  const canHaveChildren = node.depth < 2  // depth 0/1/2 = three levels max

  return (
    <div className={`mb-0.5 ${dimmed ? 'opacity-50' : ''}`}>
      <div
        className={`group/folder w-full flex items-center justify-between rounded-md hover:bg-[color:var(--me-hover)] ${isTop ? 'pl-0 pr-1 py-1' : 'px-1 py-1'}`}
        data-selected={selectedFolderId === node.id ? 'true' : undefined}
      >
        {!editing ? (
          <button
            onClick={() => onToggle(node.id)}
            className="flex items-center gap-1 flex-1 min-w-0"
          >
            <ChevronRight size={11} className={`transition-transform ${isOpen ? 'rotate-90' : ''} me-text-mute shrink-0`} />
            <FolderIcon size={12} className="me-text-mute shrink-0" />
            <span className="font-medium me-text truncate text-[12px]">{node.name}</span>
            {node.created_by_ai && (
              <span
                className="inline-flex shrink-0 cursor-help p-0.5"
                title={`AI created: ${new Date(node.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}`}
                aria-label="AI-created"
              >
                <Sparkles size={10} className="me-text-mute" />
              </span>
            )}
          </button>
        ) : (
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { update.mutate({ name }); setEditing(false) }
              if (e.key === 'Escape') { setName(node.name); setEditing(false) }
            }}
            onBlur={() => { if (name !== node.name) update.mutate({ name }); setEditing(false) }}
            className="me-input text-[12px] flex-1"
          />
        )}
        <div className="flex items-center gap-0.5">
          {/* Tools render to the LEFT of the count chip so the count's
              position stays put when hovering — the chip doesn't jump
              over to make room. */}
          <div className="hidden group-hover/folder:flex items-center gap-0.5">
            {canHaveChildren && (
              <button
                onClick={(e) => { e.stopPropagation(); setNewSubOpen(true) }}
                title="Add sub-folder"
                className="me-btn me-btn-outline !text-[10px] !px-1 !py-0.5"
              >
                <Plus size={10} />
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); setEditing(true) }}
              title="Rename"
              className="me-btn me-btn-outline !text-[10px] !px-1 !py-0.5"
            >
              <Pencil size={10} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setConfirmDeleteOpen(true) }}
              title="Delete folder"
              className="me-btn me-btn-outline !text-[10px] !px-1 !py-0.5 text-[color:var(--me-stale)]"
            >
              <Trash2 size={10} />
            </button>
          </div>
          <span className="me-chip me-mono text-[10px]">{displayedCount.toLocaleString()}</span>
        </div>
      </div>

      {newSubOpen && (
        <div className="ml-4 mt-1">
          <NewFolderInline
            parentId={node.id}
            onSubmit={(n) => { create.mutate({ name: n }); setNewSubOpen(false) }}
            onCancel={() => setNewSubOpen(false)}
          />
        </div>
      )}

      {isOpen && (
        // Children indent: top-level uses pl-3 to nudge matter content
        // ~5px past the folder icon. Sub-folders need pl-4 because the
        // folder icon shifts +16px per level (px-1 + chevron + gap +
        // icon + gap), but children-pl-3 only shifts +12px — so without
        // this bump, sub-folder matters end up flush with their parent's
        // folder icon. pl-4 restores the same +5px visual offset at
        // every depth.
        // Order: sub-folders ALWAYS render before matters so the tree
        // reads top-down: container groups first, leaf items last.
        <div className={`mt-0.5 ${isTop ? 'pl-3' : 'pl-4'}`}>
          {/* Sub-folders first. */}
          {(node.children || []).map((child) => (
            <FolderRow
              key={child.id}
              node={child}
              openSet={openSet}
              onToggle={onToggle}
              mattersByFolder={mattersByFolder}
              selectedMatterId={selectedMatterId}
              selectedFolderId={selectedFolderId}
              onSelectMatter={onSelectMatter}
              onArchiveMatter={onArchiveMatter}
              searchActive={searchActive}
              folderDistinctMatters={folderDistinctMatters}
              seenAtLoad={seenAtLoad}
              markSeen={markSeen}
            />
          ))}
          {/* Matters in THIS folder, after sub-folders. */}
          {matters.map((m) => (
            <MatterRowCompact
              key={`${m.id}@${node.id}`}
              matter={m}
              selected={selectedMatterId === m.id && selectedFolderId === node.id}
              onSelect={() => onSelectMatter(m.id, node.id)}
              isNew={seenAtLoad != null && !seenAtLoad.has(m.id)}
              markSeen={markSeen}
              onArchive={onArchiveMatter ? () => onArchiveMatter(m.id) : undefined}
            />
          ))}
          {matters.length === 0 && (node.children || []).length === 0 && (
            <div className="px-2 py-1 text-[10.5px] me-text-mute italic">empty</div>
          )}
        </div>
      )}
      {confirmDeleteOpen && (
        <ConfirmDeleteFolderModal
          folderName={node.name}
          matterCount={node.matter_count_recursive}
          childCount={(node.children || []).length}
          onCancel={() => setConfirmDeleteOpen(false)}
          onConfirm={() => { remove.mutate(); setConfirmDeleteOpen(false) }}
          pending={remove.isPending}
        />
      )}
    </div>
  )
}

/** Generic two-button confirm modal. Used for archive/restore matter
 *  actions and any other small "are you sure?" flow that wants to
 *  match the folder-delete modal's look (instead of native window
 *  confirm dialogs, which look out of place and don't theme). */
export function ConfirmActionModal({
  title, body, confirmLabel, onCancel, onConfirm, pending, danger,
}: {
  title: string
  body: ReactNode
  confirmLabel: string
  onCancel: () => void
  onConfirm: () => void
  pending?: boolean
  danger?: boolean
}) {
  return (
    <div
      className="fixed inset-0 z-[1000] grid place-items-center bg-black/30 p-4"
      onClick={onCancel}
    >
      <div
        className="me-card w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
      >
        <div className="mb-3 text-[14px] font-semibold me-text">{title}</div>
        <div className="text-[12.5px] me-text-dim">{body}</div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onCancel} className="me-btn me-btn-outline" disabled={pending}>
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="me-btn me-btn-primary"
            disabled={pending}
            style={danger ? { background: 'var(--me-stale)' } : undefined}
          >
            {pending ? '…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

function ConfirmDeleteFolderModal({
  folderName, matterCount, childCount, onCancel, onConfirm, pending,
}: {
  folderName: string
  matterCount: number
  childCount: number
  onCancel: () => void
  onConfirm: () => void
  pending: boolean
}) {
  return (
    <div
      className="fixed inset-0 z-[1000] grid place-items-center bg-black/30 p-4"
      onClick={onCancel}
    >
      <div
        className="me-card w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Trash2 size={16} className="text-[color:var(--me-stale)]" />
          <div className="text-[14px] font-semibold me-text">
            Delete folder "{folderName}"?
          </div>
        </div>
        <div className="text-[12.5px] me-text-dim space-y-2">
          <p>Matters won't be deleted:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Matters that had this as their <em>only</em> folder will move to{' '}
              <span className="me-mono">Unfiled</span>.
            </li>
            <li>
              Matters filed in multiple folders only lose the link to this one.
            </li>
            {childCount > 0 && (
              <li>
                {childCount} sub-folder{childCount === 1 ? '' : 's'} will be reparented
                to this folder's parent (or become top-level).
              </li>
            )}
            {matterCount > 0 && (
              <li>
                Currently <span className="me-mono">{matterCount.toLocaleString()}</span> matter
                {matterCount === 1 ? '' : 's'} filed in this folder
                {childCount > 0 ? ' or its sub-folders' : ''}.
              </li>
            )}
          </ul>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="me-btn me-btn-outline"
            disabled={pending}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="me-btn me-btn-primary"
            disabled={pending}
            style={{ background: 'var(--me-stale)' }}
          >
            {pending ? 'Deleting…' : 'Delete folder'}
          </button>
        </div>
      </div>
    </div>
  )
}

function NewFolderInline({
  parentId, onSubmit, onCancel,
}: {
  parentId: number | null
  onSubmit: (name: string) => void
  onCancel: () => void
}) {
  void parentId
  const [name, setName] = useState('')
  return (
    <div className="me-card p-1.5 mb-1">
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && name.trim()) onSubmit(name.trim())
          if (e.key === 'Escape') onCancel()
        }}
        placeholder="folder name…"
        className="me-input text-[12px]"
      />
      <div className="mt-1 flex justify-end gap-1">
        <button onClick={onCancel} className="me-btn me-btn-outline !text-[10px] !py-0.5">Cancel</button>
        <button
          onClick={() => name.trim() && onSubmit(name.trim())}
          disabled={!name.trim()}
          className="me-btn me-btn-primary !text-[10px] !py-0.5"
        >Create</button>
      </div>
    </div>
  )
}

export function MatterRowCompact({
  matter, selected, onSelect, onArchive, onRestore, archived, isNew, markSeen,
}: {
  matter: Matter
  selected: boolean
  onSelect: () => void
  onArchive?: () => void
  onRestore?: () => void
  /** True when rendering inside the Archived section. Dims the row
   *  and swaps the archive button for a restore button. */
  archived?: boolean
  /** True when this matter wasn't present on the page-load snapshot —
   *  i.e. it was created by AI (or another user) while the workspace
   *  has been open. The name flashes once on first appearance. */
  isNew?: boolean
  /** Called once on first mount when isNew=true so the parent can
   *  promote this id out of "new" status — prevents the flash from
   *  re-firing on subsequent unmount/remount (e.g. folder collapse). */
  markSeen?: (id: number) => void
}) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const currentFolderIds = new Set((matter.folder_assignments || []).map((a) => a.folder))
  const code = matter.composite_id || `M-${matter.client_seq.toString().padStart(3, '0')}`
  // Flash on AI updates so the user notices a change happened while they
  // sat on this view. The name flash also fires on first mount when the
  // matter is brand-new (isNew) — surfaces AI-driven matter creation that
  // happens via background poll. See useFlashOnChange.
  const nameFlash = useFlashOnChange(matter.name, matter.id, { flashOnFirstMount: isNew })
  const stageFlash = useFlashOnChange(matter.stage, matter.id)
  // After the introduction-flash plays once, tell the parent this id is
  // "seen" so a later unmount/remount (folder collapse, filter change)
  // doesn't trigger the flash a second time. Mount-only effect; matter.id
  // is stable per row (key is keyed by id), so this fires exactly once.
  useEffect(() => {
    if (isNew && markSeen) markSeen(matter.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // Layout matches the original (pre-folder-tree) workspace MatterRow:
  // line 1 = matter name, line 2 = code · type · stage. Summary is NOT
  // shown in the list — too cramped to be useful, and the matter detail
  // panel surfaces it. No title-attribute tooltip either.
  return (
    <div className={`relative group/matter mb-0.5 ${archived ? 'opacity-70' : ''}`}>
      <button
        onClick={onSelect}
        data-selected={selected ? 'true' : undefined}
        className="block w-full text-left rounded-md pl-2 pr-1 py-1 hover:bg-[color:var(--me-hover)] data-[selected=true]:bg-[color:var(--me-selected)]"
      >
        <div className="flex items-center gap-1.5">
          <span className={`font-medium me-text truncate text-[13px] ${nameFlash ? 'me-flash' : ''}`}>{matter.name}</span>
        </div>
        <div className="me-mono mt-0.5 truncate text-[11px] me-text-mute">
          {code} · {matter.matter_type}
          {matter.stage && <> · <span className={stageFlash ? 'me-flash' : ''}>{matter.stage.replace(/_/g, ' ')}</span></>}
        </div>
      </button>
      {/* Hover toolbar: vertical stack of icons on right edge, with a
          gradient fade so the title fades into the action column rather
          than being hard-cut. Pointer-events-none on the gradient so the
          row is still clickable across the fade region; pointer-events-
          auto on the icon column so the icons themselves are clickable. */}
      <div className="absolute right-0 top-0 bottom-0 hidden group-hover/matter:flex items-stretch pointer-events-none rounded-r-md overflow-hidden">
        <div
          className="w-4"
          style={{
            background: selected
              ? 'linear-gradient(to right, rgba(0,0,0,0), var(--me-selected) 90%)'
              : 'linear-gradient(to right, rgba(0,0,0,0), var(--me-hover) 90%)',
          }}
        />
        <div
          className="flex flex-col items-end justify-center gap-0.5 px-1 pointer-events-auto"
          style={{ background: selected ? 'var(--me-selected)' : 'var(--me-hover)' }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setPickerOpen(true) }}
            className="me-btn me-btn-outline !text-[10px] !px-1 !py-0.5"
            title="Manage folders for this matter"
          >
            <FolderPlus size={10} />
          </button>
          {onArchive && !archived && (
            <button
              onClick={(e) => { e.stopPropagation(); setArchiveOpen(true) }}
              className="me-btn me-btn-outline !text-[10px] !px-1 !py-0.5"
              title="Archive this matter"
            >
              <Archive size={10} />
            </button>
          )}
          {onRestore && archived && (
            <button
              onClick={(e) => { e.stopPropagation(); onRestore() }}
              className="me-btn me-btn-outline !text-[10px] !px-1 !py-0.5"
              title="Restore this matter from archive"
            >
              <RotateCcw size={10} />
            </button>
          )}
        </div>
      </div>
      {pickerOpen && (
        <FolderPickerModal
          matter={matter}
          currentFolderIds={currentFolderIds}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {archiveOpen && onArchive && (
        <ConfirmActionModal
          title={`Archive "${matter.name}"?`}
          body={<>It moves to the <span className="me-mono">Archived</span> section and stays accessible. You can restore it any time.</>}
          confirmLabel="Archive"
          onCancel={() => setArchiveOpen(false)}
          onConfirm={() => { onArchive(); setArchiveOpen(false) }}
        />
      )}
    </div>
  )
}

// ─── Folder picker (popover for moving a matter into folder(s)) ─────

export function FolderPickerModal({
  matter, currentFolderIds, onClose,
}: {
  matter: Matter
  currentFolderIds: Set<number>
  onClose: () => void
}) {
  const matterId = matter.id
  const tree = useQuery({
    queryKey: ['folders', 'tree'],
    queryFn: () => foldersApi.tree(),
    staleTime: 30_000,
  })
  const qc = useQueryClient()

  const [selected, setSelected] = useState<Set<number>>(new Set(currentFolderIds))
  // Track "new folder" UI visibility for top-level adds.
  const [showNewTop, setShowNewTop] = useState(false)

  const toggleSel = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const create = useMutation({
    mutationFn: (payload: { name: string; parent_folder?: number | null }) => foldersApi.create(payload),
    onSuccess: (folder) => {
      qc.invalidateQueries({ queryKey: ['folders', 'tree'] })
      setSelected((prev) => new Set([...prev, folder.id]))
    },
  })
  const assign = useMutation({
    mutationFn: (folder: number) => foldersApi.assignMatter(matterId, folder),
  })
  const dismiss = useMutation({
    mutationFn: (folder: number) => foldersApi.dismissMatter(matterId, folder),
  })

  // Compute diff vs initial state. Save is only meaningful when there's
  // an actual change (added or removed assignments).
  const hasDiff = useMemo(() => {
    if (selected.size !== currentFolderIds.size) return true
    for (const id of selected) if (!currentFolderIds.has(id)) return true
    return false
  }, [selected, currentFolderIds])

  const apply = async () => {
    const toAdd = [...selected].filter((id) => !currentFolderIds.has(id))
    const toRemove = [...currentFolderIds].filter((id) => !selected.has(id))
    await Promise.all([
      ...toAdd.map((id) => assign.mutateAsync(id)),
      ...toRemove.map((id) => dismiss.mutateAsync(id)),
    ])
    // refetchQueries (not just invalidate) so the workspace's matter list
    // and the picker's tree both refresh BEFORE the popover closes.
    await Promise.all([
      qc.refetchQueries({ queryKey: ['matters'] }),
      qc.refetchQueries({ queryKey: ['folders', 'tree'] }),
    ])
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-[1000] grid place-items-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="me-card w-full max-w-sm p-3 shadow-lg"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
      >
        <div className="mb-2">
          <div className="text-[13px] font-semibold me-text">File matter in folders</div>
          <div className="me-text-mute text-[11px] truncate" title={matter.name}>
            <span className="me-mono">{matter.composite_id}</span> · {matter.name}
          </div>
        </div>
      <div className="max-h-72 overflow-auto pr-1">
        {tree.data && (
          <FolderPickerList
            nodes={tree.data}
            selected={selected}
            onToggle={toggleSel}
            onCreateChild={(parentId, name) => create.mutate({ name, parent_folder: parentId })}
          />
        )}
      </div>
      <div className="mt-2 border-t me-border pt-1.5">
        {!showNewTop ? (
          <button
            onClick={() => setShowNewTop(true)}
            className="me-btn me-btn-outline !text-[11px] w-full justify-center"
          >
            <Plus size={10} /> New top-level folder
          </button>
        ) : (
          <NewFolderInline
            parentId={null}
            onSubmit={(name) => { create.mutate({ name, parent_folder: null }); setShowNewTop(false) }}
            onCancel={() => setShowNewTop(false)}
          />
        )}
      </div>
      <div className="mt-2 flex justify-end gap-1.5">
        <button onClick={onClose} className="me-btn me-btn-outline !text-[11px]">Cancel</button>
        <button
          onClick={apply}
          disabled={!hasDiff || assign.isPending || dismiss.isPending}
          className="me-btn me-btn-primary !text-[11px]"
          title={!hasDiff ? 'No changes to save' : ''}
        >
          {assign.isPending || dismiss.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      </div>
    </div>
  )
}

function FolderPickerList({
  nodes, selected, onToggle, onCreateChild, indent = 0,
}: {
  nodes: FolderNode[]
  selected: Set<number>
  onToggle: (id: number) => void
  onCreateChild: (parentId: number, name: string) => void
  indent?: number
}) {
  return (
    <div>
      {nodes.map((n) => (
        <FolderPickerRow
          key={n.id}
          node={n}
          selected={selected}
          onToggle={onToggle}
          onCreateChild={onCreateChild}
          indent={indent}
        />
      ))}
    </div>
  )
}

function FolderPickerRow({
  node, selected, onToggle, onCreateChild, indent,
}: {
  node: FolderNode
  selected: Set<number>
  onToggle: (id: number) => void
  onCreateChild: (parentId: number, name: string) => void
  indent: number
}) {
  const [showAdd, setShowAdd] = useState(false)
  const canHaveChildren = node.depth < 2
  return (
    <div style={{ paddingLeft: indent * 12 }}>
      <label
        className="mb-0.5 cursor-pointer flex items-center gap-1.5 group/pickerrow hover:bg-[color:var(--me-hover)] rounded-md px-2 py-1.5"
      >
        <input
          type="checkbox"
          checked={selected.has(node.id)}
          onChange={() => onToggle(node.id)}
          className="cursor-pointer shrink-0"
        />
        <FolderIcon size={11} className="me-text-mute shrink-0" />
        <span className="text-[11.5px] me-text truncate flex-1 min-w-0">{node.name}</span>
        {node.created_by_ai && (
          <span
            className="inline-flex shrink-0 cursor-help p-0.5"
            title={`AI created: ${new Date(node.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}`}
            aria-label="AI-created"
          >
            <Sparkles size={9} className="me-text-mute" />
          </span>
        )}
        {canHaveChildren && (
          <button
            onClick={(e) => { e.preventDefault(); setShowAdd(true) }}
            className="hidden group-hover/pickerrow:inline-flex items-center text-[10px] me-text-mute shrink-0"
            title="Add sub-folder"
          >
            <Plus size={10} />
          </button>
        )}
      </label>
      {showAdd && (
        <div style={{ paddingLeft: 16 }}>
          <NewFolderInline
            parentId={node.id}
            onSubmit={(name) => { onCreateChild(node.id, name); setShowAdd(false) }}
            onCancel={() => setShowAdd(false)}
          />
        </div>
      )}
      {(node.children || []).length > 0 && (
        <FolderPickerList
          nodes={node.children}
          selected={selected}
          onToggle={onToggle}
          onCreateChild={onCreateChild}
          indent={indent + 1}
        />
      )}
    </div>
  )
}
